'use client';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTablePagination } from '../../documents/table/pagination';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CircularProgress } from '@mui/material';
import type { FileKnowledge } from '@/graphql/types/chatKnowledge';
import { KnowledgeDataTable } from './knowledge-data-table';
import { fileKnowledgeColumns } from './knowledge-columns';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onRowClick?: (row: TData) => void;
  onSearchChange?: (search: string) => void;
  expandedFileId?: string | null;
  knowledgeByFileId?: Record<string, FileKnowledge[]>;
  loadingKnowledge?: Record<string, boolean>;
  onKnowledgeRowClick?: (knowledge: FileKnowledge) => void;
}

export function ContextFilesDataTable<TData extends { id: string }, TValue>({
  columns,
  data,
  onRowClick,
  onSearchChange,
  expandedFileId,
  knowledgeByFileId = {},
  loadingKnowledge = {},
  onKnowledgeRowClick,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
  });

  const columnNormalizer = (id: string) => {
    const columnNames: Record<string, string> = {
      original_name: 'Nome do Arquivo',
      filename: 'Nome do Sistema',
      knowledge_count: 'Conhecimentos',
      category: 'Categoria',
      municipality_code: 'Município',
      file_size: 'Tamanho',
      created_at: 'Data de Upload',
    };
    return columnNames[id] || id;
  };

  return (
    <div className="flex flex-col gap-4 bg-white rounded-lg p-2 sm:p-4 border border-slate-200">
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2">
        <Input
          placeholder="Buscar"
          value={globalFilter ?? ''}
          onChange={(event) => {
            const value = String(event.target.value);
            setGlobalFilter(value);
            onSearchChange?.(value);
          }}
          className="w-full"
        />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full sm:w-auto sm:ml-auto sm:max-w-xs whitespace-nowrap"
            >
              Colunas
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                if (column.id === 'actions') return null;
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                  >
                    {columnNormalizer(column.id)}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="w-full overflow-x-auto overflow-y-hidden rounded-md border">
        <Table className="min-w-[640px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const fileId = (row.original as { id: string }).id;
                const isExpanded = expandedFileId === fileId;
                const fileKnowledge = knowledgeByFileId[fileId] || [];
                const isLoading = loadingKnowledge[fileId];

                return (
                  <>
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      onClick={(e) => {
                        // Don't trigger row click if clicking on actions dropdown
                        const target = e.target as HTMLElement;
                        if (
                          target.closest('[role="menuitem"]') ||
                          target.closest('[role="menu"]') ||
                          target.closest('[data-radix-popper-content-wrapper]') ||
                          (target.tagName === 'BUTTON' && target.closest('td:last-child'))
                        ) {
                          return;
                        }
                        onRowClick?.(row.original);
                      }}
                      className={onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}
                    >
                      {row.getVisibleCells().map((cell, index) => {
                        // Add expand icon to first cell
                        if (index === 0) {
                          return (
                            <TableCell key={cell.id}>
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </div>
                              </div>
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`${row.id}-expanded`}>
                        <TableCell colSpan={columns.length} className="bg-gray-50 p-4">
                          {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                              <CircularProgress size={24} />
                              <span className="ml-2 text-sm text-gray-600">
                                Carregando conhecimentos...
                              </span>
                            </div>
                          ) : fileKnowledge.length > 0 ? (
                            <div>
                              <h4 className="font-semibold text-sm mb-4">
                                Conhecimentos Gerados ({fileKnowledge.length})
                              </h4>
                              <KnowledgeDataTable
                                columns={fileKnowledgeColumns}
                                data={fileKnowledge}
                                onRowClick={onKnowledgeRowClick}
                              />
                            </div>
                          ) : (
                            <div className="text-center py-8 text-sm text-gray-500">
                              Nenhum conhecimento gerado para este arquivo.
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Nenhum resultado encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end space-x-2">
        <DataTablePagination table={table} />
      </div>
    </div>
  );
}
